import {ellipsis} from 'polished';
import React, {FC, PropsWithChildren} from 'react';
import styled, {css} from 'styled-components';

import {Renderer} from '../../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {
  Skeleton,
  SkeletonColorSchemesEnum,
} from '../../../../../libs/shared/ui/src/components/utility/skeleton';
import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';

/*
 * Props.
 */

interface SimpleGridLayoutProps {
  children: React.ReactNode;
  title?: React.ReactNode;
  description?: React.ReactNode;
  action?: React.ReactNode;
  shouldHideOverflowingItems?: boolean;
  shouldHaveLoadingGradient?: boolean;
  isLoading?: boolean;
  className?: string;

  renderLoadingState?: Renderer;
}

/*
 * Style.
 */

const StyledGridContainerWrapperDiv = styled.div`
  display: flex;
  flex-flow: column;
  gap: 24px;
  margin-bottom: 40px;
`;

const StyledGridHeaderDiv = styled.div`
  display: flex;
  flex-flow: row;
  gap: 16px;
`;

const StyledGridTextWrapperDiv = styled.div`
  display: flex;
  flex-flow: column;
  gap: 4px;
  flex: 1;
  overflow: hidden;
`;

const StyledGridHeaderTitleDiv = styled.div`
  font-weight: ${fontWeights.semibold};
  font-size: ${fontSizes.veryLarge};
  ${ellipsis()};
`;

const StyledGridHeaderDescriptionDiv = styled.div`
  color: ${(p) => p.theme.greys.shade80};
  ${ellipsis()};
`;

const StyledActionWrapperDiv = styled.div`
  margin-top: -3px;
  margin-right: -14px;
`;

interface StyledFlexWrapperDivProps {
  $shouldHaveLoadingGradient?: boolean;
}

const StyledFlexWrapperDiv = styled.div<StyledFlexWrapperDivProps>`
  display: flex;
  flex-flow: row;
  flex-wrap: wrap;
  gap: 24px;

  ${(p) =>
    p.$shouldHaveLoadingGradient &&
    css`
      position: relative;

      &::after {
        content: '';
        position: absolute;
        bottom: -5px;
        left: -5px;
        right: -5px;
        top: 0;
        background: linear-gradient(
          180deg,
          rgba(255, 255, 255, 0) 0%,
          rgba(255, 255, 255, 0) 10%,
          ${p.theme.foreground} 90%,
          ${p.theme.foreground} 100%
        );
      }
    `};

  & > * {
    flex: 0 0 calc(25% - 18px);
    overflow: hidden;
  }

  @media only screen and (min-width: 900px) and (max-width: 1100px) {
    & > * {
      flex: 0 0 calc(33.33% - 16px);
    }
  }
  @media only screen and (max-width: 900px) {
    & > * {
      flex: 0 0 calc(50% - 12px);
    }
  }
`;

const StyledGridWrapperDiv = styled.div`
  display: grid;
  grid-auto-columns: minmax(0, 1fr);
  grid-auto-flow: column;
  gap: 24px;

  @media only screen and (max-width: 900px) {
    & > *:nth-child(3) {
      display: none;
    }
  }
  @media only screen and (max-width: 1100px) {
    & > *:nth-child(4) {
      display: none;
    }
  }
`;

/*
 * Component.
 */

export const SimpleGridLayout: FC<PropsWithChildren<SimpleGridLayoutProps>> = (props) => {
  const {
    title,
    description,
    action,
    children,
    shouldHideOverflowingItems,
    shouldHaveLoadingGradient,
    isLoading,
    className,
    renderLoadingState,
  } = props;

  if (isLoading && renderLoadingState) {
    return (
      <StyledGridContainerWrapperDiv>
        {maybeRenderLoadingHeader(title, description)}
        {renderWrapper(renderLoadingState(), shouldHideOverflowingItems, shouldHaveLoadingGradient)}
      </StyledGridContainerWrapperDiv>
    );
  }
  return (
    <StyledGridContainerWrapperDiv className={className}>
      {maybeRenderHeader(title, description, action)}
      {renderWrapper(children, shouldHideOverflowingItems)}
    </StyledGridContainerWrapperDiv>
  );
};

/*
 * Helpers.
 */

function renderWrapper(
  children: React.ReactNode,
  shouldHideOverflowingItems?: boolean,
  shouldHaveLoadingGradient?: boolean,
) {
  if (shouldHideOverflowingItems) {
    return <StyledGridWrapperDiv>{children}</StyledGridWrapperDiv>;
  }
  return (
    <StyledFlexWrapperDiv $shouldHaveLoadingGradient={shouldHaveLoadingGradient}>
      {children}
    </StyledFlexWrapperDiv>
  );
}

function maybeRenderHeader(title?: React.ReactNode, description?: React.ReactNode, action?: React.ReactNode) {
  if (!title && !description) {
    return null;
  }
  return (
    <StyledGridHeaderDiv>
      <StyledGridTextWrapperDiv>
        {title && <StyledGridHeaderTitleDiv>{title}</StyledGridHeaderTitleDiv>}
        {description && <StyledGridHeaderDescriptionDiv>{description}</StyledGridHeaderDescriptionDiv>}
      </StyledGridTextWrapperDiv>
      {action && <StyledActionWrapperDiv>{action}</StyledActionWrapperDiv>}
    </StyledGridHeaderDiv>
  );
}

function maybeRenderLoadingHeader(title?: React.ReactNode, description?: React.ReactNode) {
  if (!title && !description) {
    return null;
  }
  return (
    <StyledGridHeaderDiv>
      <StyledGridTextWrapperDiv>
        {title && <Skeleton width="20%" height="22px" scheme={SkeletonColorSchemesEnum.GREY30} />}
        {description && <Skeleton width="35%" height="16px" scheme={SkeletonColorSchemesEnum.GREY30} />}
      </StyledGridTextWrapperDiv>
    </StyledGridHeaderDiv>
  );
}
